import { UserTypes } from '../enums/Roles';
import { StatusOptionsEnum } from '../enums/StatusOptions';
import { Region } from './region';
import { getUserStatus, getUserType } from './token';

export const isDevelopment = import.meta.env.MODE === 'development';
export const isProduction = import.meta.env.VITE_APP_ENV?.includes('prod');
export const isIndividual = import.meta.env.VITE_PLATFORM === 'individual';
export const isAZRegion = import.meta.env.VITE_REGION === Region.AZ;

export const setStorageSidebarOpen = (isOpen: boolean) => {
  window.localStorage.setItem('sidebarOpen', isOpen.toString());
};

export const localStorageSidebarState = {
  get: () => {
    return window.localStorage.getItem('sidebarOpen') === 'true';
  },
  set: (isOpen: boolean) => {
    window.localStorage.setItem('sidebarOpen', isOpen.toString());
  },
};

export const isMobileClientSide =
  typeof window !== 'undefined' &&
  /android.+mobile|ip(hone|[oa]d)/i.test(navigator.userAgent);

export const isSafari = () => {
  if (typeof window === 'undefined') return false;

  const userAgent = navigator.userAgent;
  const isSafariUA = /^((?!chrome|android).)*safari/i.test(userAgent);
  const isIOS = /iPad|iPhone|iPod/.test(userAgent);

  return isSafariUA || isIOS;
};

export const isFullURL = (str: string) =>
  str.indexOf('http://') === 0 || str.indexOf('https://') === 0;

export const getScreenSize = () => {
  if (typeof window !== 'undefined') {
    const width = window.innerWidth;

    if (width < 768) {
      return 'mobile';
    } else if (width < 1024) {
      return 'tablet';
    } else {
      return 'desktop';
    }
  }

  return null;
};

export const getScreenSizeColumns = () => {
  const screenSize = getScreenSize();

  const columns: { [key in 'mobile' | 'tablet' | 'desktop']: number } = {
    mobile: 1,
    tablet: 2,
    desktop: 4,
  };

  return screenSize ? columns[screenSize] : 4;
};

export const checkIsParent = () =>
  getUserType() === UserTypes.PARENT || getUserType() === UserTypes.EMPLOYEE;

export const checkIsRestricted = () =>
  getUserStatus() === StatusOptionsEnum.RESTRICTED;

export const checkTahtaAccess = (myInfo?: {
  studentInfo: { organization: string; grade: string };
}) => {
  if (!myInfo?.studentInfo) {
    return false;
  }

  const { organization, grade } = myInfo.studentInfo;

  const allowedOrganizations = [
    'Nesibe Aydın Okulları',
    'Serçe Akademi',
    'Kutlubey Eğitim Kurumları',
    'Aydın Kurs',
  ];

  if (!allowedOrganizations.includes(organization)) {
    return false;
  }

  const allowedGrades = ['7', '8', '9', '10', '11', '12', 'Mezun'];
  return allowedGrades.includes(grade);
};

export const checkOkumanyaAccess = (myInfo?: {
  studentInfo: { organization: string; grade: string };
}) => {
  if (!myInfo?.studentInfo) {
    return false;
  }

  const { organization, grade } = myInfo.studentInfo;
  const allowedOrganizations = ['Mavi Dünya Okulları'];

  if (!allowedOrganizations.includes(organization)) {
    return false;
  }

  const allowedGrades = [
    '4-5 Yaş',
    '5-6 Yaş',
    '1',
    '2',
    '3',
    '4',
    '5',
    '6',
    '7',
    '9',
    '10',
    '11',
  ];
  return allowedGrades.includes(grade);
};

export const checkPdevcAccess = (myInfo?: {
  studentInfo: { organization: string; grade: string };
}) => {
  if (!myInfo?.studentInfo) {
    return false;
  }

  const { organization, grade } = myInfo.studentInfo;
  const allowedOrganizations = [
    'Mavi Dünya Okulları',
    'Nesibe Aydın Okulları',
    'Aydın Kurs',
  ];

  if (!allowedOrganizations.includes(organization)) {
    return false;
  }

  const allowedGrades = [
    '1',
    '2',
    '3',
    '4',
    '5',
    '6',
    '7',
    '8',
    '9',
    '10',
    '11',
    '12',
  ];
  return allowedGrades.includes(grade);
};

//get the file name before the uuid, if not, return the file download text
export const formatFileName = (path: string) => {
  if (!path) return '';

  const segments = path.split('/');
  const fileName = segments[segments.length - 1];

  const uuidPattern =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const nameAndUuid = fileName.split('_');

  if (nameAndUuid.length > 1) {
    const possibleUuid = nameAndUuid[nameAndUuid.length - 1].split('.')[0];
    if (uuidPattern.test(possibleUuid)) {
      return fileName.replace(`_${possibleUuid}`, '');
    }
  }

  const fileNameWithoutExt = fileName.split('.')[0];
  if (uuidPattern.test(fileNameWithoutExt)) {
    return 'Dosya İndir';
  }

  return fileName;
};

export const DEFAULT_IMAGE_URL =
  'https://static.vecteezy.com/system/resources/thumbnails/039/228/317/small_2x/empty-blank-schoolboard-background-school-board-template-photo.jpg';

export const getLessonImageUrl = (coverImage?: string) => {
  return coverImage
    ? `${import.meta.env.VITE_CDN_URL}/${coverImage}`
    : DEFAULT_IMAGE_URL;
};

/**
 * Checks if the user is on one of the public authentication pages.
 * Public auth pages: /signin, /forgot-password, /reset-password, /sl/*
 * @param pathname - Path to check
 * @returns boolean
 */
export const isOnPublicAuthPage = (pathname: string) => {
  return (
    pathname === '/signin' ||
    pathname === '/forgot-password' ||
    pathname === '/reset-password' ||
    pathname.startsWith('/sl/')
  );
};

export const clamp = (v: number, min: number, max: number) =>
  Math.min(max, Math.max(min, v));
